// Seizure's inspector (spec §15, design's screen): the selected parcel's strongest connections in the
// run's own structural network, each a link to its region with its weight; the tooltip carries the
// tract lengths, the model's delays on them and the coupling they enter with. ba-activity's handoff
// (#2, 2026-10-02): read the node's actual row, not design's placeholder names.

import type { ReactNode } from 'react';
import { useAtlas } from '../app/store.ts';
import { openedSlot } from '../app/sims.ts';
import type { DynamicsSim } from '../data/sims/dynamics.ts';
import { formatValue } from '../ui/chart.ts';
import { useLoaded } from '../ui/hooks.ts';
import { parcelRegion, Region } from './ElectrodePanel.tsx';
import { mainRun } from './ElectrodeSites.tsx';
import { degree, loadNetwork, nodeOf, strongest } from './network.ts';

/** "A, B and C" from parts. */
const list = (parts: ReactNode[]): ReactNode[] => parts.flatMap((p, i) => (i === 0 ? [p] : [i === parts.length - 1 ? ' and ' : ', ', p]));

/** A region's name inside a sentence: the pack's "Left …" reads "left …" there. */
const inSentence = (name: string) => name.replace(/^(Left|Right) /, (m) => m.toLowerCase());

export function ConnectionRow({ structure }: { structure: string }) {
  const catalogue = useAtlas((s) => s.catalogue);
  const slot = useAtlas(mainRun);
  const o = slot ? openedSlot('main') : null;
  const sim = o?.format === 'dynamics' ? (o.sim as DynamicsSim) : null;
  // Keyed on the run, not the opened object: a re-render must not reopen the file.
  const net = useLoaded(() => (sim ? loadNetwork(sim) : null), [slot?.path, sim]);
  if (!sim || !net.value) return null;
  const i = nodeOf(net.value, sim, structure);
  if (i === null) return null;
  const top = strongest(net.value, i, 3);
  if (!top.length) return null;
  const n = net.value;
  const coupling = [n.coupling_multiplier !== undefined ? `G = ${n.coupling_multiplier}` : null, n.coupling_parameters?.Ks !== undefined ? `Ks = ${n.coupling_parameters.Ks}` : null].filter(Boolean).join(', ');
  const title = [
    'Normalised fibre density in the run’s structural network (1 is its densest pair), with each tract’s length and the model’s delay on it:',
    ...top.map((c) => {
      const r = parcelRegion(sim, catalogue, c.index);
      return `${r.name} ${formatValue(c.weight, 2)}${c.lengthMm !== null ? `, ${Math.round(c.lengthMm)} mm` : ''}${c.delayMs !== null ? `, ${formatValue(c.delayMs, 1)} ms` : ''}`;
    }),
    `${degree(n, i)} connections in all${coupling ? `; the coupling enters as ${coupling}` : ''}${n.conduction_speed_m_s ? `, delays at ${n.conduction_speed_m_s} m/s` : ''}.`,
  ].join('\n');
  return (
    <div className="insp-live-row insp-connections">
      <dt title="The run’s structural network: its strongest links from this region">Connections</dt>
      <dd className="insp-wide" title={title}>
        Strongest to{' '}
        {list(
          top.map((c) => {
            const r = parcelRegion(sim, catalogue, c.index);
            return (
              <span key={c.index} className="insp-conn">
                <Region id={r.id} name={inSentence(r.name)} /> <span className="muted num">{formatValue(c.weight, 2)}</span>
              </span>
            );
          }),
        )}
      </dd>
    </div>
  );
}
